import * as Yup from 'yup';
import { AppDispatch } from '@/app/store';
import { AuthorDTO } from '@/types/AuthorDTO';
import { Button, Grid, Theme } from '@mui/material';
import { CreateFormAnswerProps } from '@/types/create/CreateFormAnswerProps';
import { CreateFormValues } from '@/types/create/CreateFormValues';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { FixedButton } from '@/components/common/button/FixedButton';
import { Formik, FormikProps } from 'formik';
import { MobileBar } from '@/components/home/MobileBar';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { RegionDTO } from '@/types/RegionDTO';
import { SenderDTO } from '@/types/SenderDTO';
import { SenderForm } from '@/components/create/SenderForm';
import { TittleInfo } from '@/components/create/TittleInfo';
import { UserDTO } from '@/types/user/UserDTO';
import { answersCountSchema } from '@/validation/answersCountSchema';
import { clearBalanceByLetterPageState } from '@/services/sbpSlice';
import { formatUserFullName, removeExtraSpaces } from '@/utils/string-utils';
import {
  getAnswersCount,
  getAuthor,
  getDepartment,
  getRegion,
  getSender,
  setAnswerOrderedForm,
  setAnswersCountForm,
  setAuthor,
  setSender,
} from '@/services/createFormSlice';
import { getCurrentUser } from '@/services/authSlice';
import { prisonerMiddleNameSchema } from '@/validation/prisonerMiddleNameSchema';
import { prisonerNameSchema } from '@/validation/prisonerNameSchema';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import { yearSchema } from '@/validation/yearSchema';
import React, { ChangeEvent, FC, ReactElement, useEffect } from 'react';

const validationSchema = Yup.object({
  prisonerLastName: prisonerNameSchema,
  prisonerFirstName: prisonerNameSchema,
  prisonerMiddleName: prisonerMiddleNameSchema,
  prisonerBirthDate: yearSchema,
  answersCount: answersCountSchema,
});

export const CreateFormSender: FC<CreateFormAnswerProps> = (props: CreateFormAnswerProps): ReactElement => {
  const {
    tittle,
    nextFormPath,
    previousFormPath,
    previousFormLabel,
    errorRedirectPath,
    isMobile,
    mobileTittle,
    fromCreateAnswer,
  } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();

  const department: DepartmentDTO | null = useAppSelector(getDepartment);
  const region: RegionDTO | null = useAppSelector(getRegion);
  const sender: SenderDTO | null = useAppSelector(getSender);
  const author: AuthorDTO | null = useAppSelector(getAuthor);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const answersCount: number = useAppSelector(getAnswersCount);

  useEffect((): void => {
    if (!(department && region)) {
      if (errorRedirectPath) {
        navigate(errorRedirectPath);
      }
    }
  }, [department, errorRedirectPath, navigate, region]);

  const { data: user } = useGetCurrentUserQuery(authUser?.id, {
    skip: !authUser,
  });

  const initialValues: CreateFormValues = {
    prisonerLastName: sender?.lastName || '',
    prisonerFirstName: sender?.firstName || '',
    prisonerMiddleName: sender?.middleName || '',
    prisonerBirthDate: sender?.age.toString() || '',
    authorFullName: author?.name || (user ? formatUserFullName(user) : ''),
    authorPhoneNumber: author?.phone || (user && user.phone ? user.phone : ''),
    authorEmail: author?.email || (user ? user.email : ''),
    authorConfirmEmail: author?.email || (user ? user.email : ''),
    answersCount: answersCount,
  };

  const handleSubmit = (values: CreateFormValues): void => {
    dispatch(
      setSender({
        lastName: removeExtraSpaces(values.prisonerLastName),
        firstName: removeExtraSpaces(values.prisonerFirstName),
        middleName: removeExtraSpaces(values.prisonerMiddleName),
        age: parseInt(String(values.prisonerBirthDate)),
      }),
    );
    if (fromCreateAnswer) {
      dispatch(setAnswerOrderedForm(true));
      dispatch(setAnswersCountForm(values.answersCount ? values.answersCount : 1));
      dispatch(
        setAuthor({
          name: removeExtraSpaces(values.authorFullName),
          phone: values.authorPhoneNumber,
          email: values.authorEmail,
        }),
      );
    }
    dispatch(clearBalanceByLetterPageState());
    if (nextFormPath) {
      navigate(nextFormPath);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnChange={true}
      onSubmit={handleSubmit}>
      {(formikProps: FormikProps<CreateFormValues>) => {
        const { submitForm, setFieldValue } = formikProps;
        const handleSetFieldValue = (field: string, value: unknown, isErrorField: boolean) => {
          //setFieldTouched(field, true, false);
          setFieldValue(field, value, isErrorField);
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleAnswersCountChange = (event: ChangeEvent<HTMLInputElement>): void => {
          const value = parseInt(event.target.value);
          if (value) {
            //setFieldTouched('answersCount', true, false);
            setFieldValue('answersCount', value, true);
          }
        };
        return (
          <Grid
            container={true}
            direction={'column'}
            sx={{
              paddingBottom: (theme: Theme) => (isMobile ? theme.spacing(6) : theme.spacing(0)),
            }}>
            <Grid item={true} mb={isMobile ? 6 : 2}>
              <PageHeader
                tittle={tittle}
                previousFormLabel={previousFormLabel}
                previousFormPath={previousFormPath}
                isMobile={isMobile}
                mobileTittle={mobileTittle}
              />
            </Grid>
            {!isMobile && (
              <Grid item={true} mb={7}>
                <TittleInfo region={region?.name} department={department?.name} />
              </Grid>
            )}
            <Grid item={true}>
              <SenderForm
                formikProps={formikProps}
                handleSetFieldValue={handleSetFieldValue}
                isMobile={isMobile}
                fromCreateAnswer={fromCreateAnswer}
              />
            </Grid>
            {isMobile ? (
              <MobileBar>
                <FixedButton color={'primary'} variant={'contained'} onClick={submitForm}>
                  {'Продолжить'}
                </FixedButton>
              </MobileBar>
            ) : (
              <Grid item={true} marginTop={'32px'}>
                <Grid container={true} direction={'row'} justifyContent={'flex-end'}>
                  <Grid item={true} xs={4}>
                    <Button
                      onClick={submitForm}
                      variant={'contained'}
                      color={'primary'}
                      fullWidth={true}
                      size={'medium'}>
                      {'Продолжить'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        );
      }}
    </Formik>
  );
};
